import React, { useEffect, useState, useCallback } from 'react';
import Modal from './ModalConversion';
import { theme } from '../theme';
import { Loading } from './Loading';
import {
    endpointMissingPublication,
    endpointMissingPublicationSummary,
    endpointMissingPublicationExport,
    urlTasks,
} from '../services/Endpoints';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faExclamationCircle,
    faChartPie,
    faTable,
    faFileExcel,
} from '@fortawesome/free-solid-svg-icons';

const PER_PAGE = 50;

const statusBadgeClass = (s) => {
    if (s === 'on') return 'badge-status status-on';
    if (s === 'off') return 'badge-status status-off';
    if (s === 'disabled') return 'badge-status status-disabled';
    return 'badge-status status-unselected';
};

// Etiqueta corta legible por razón (comparativa stock vs tienda)
const REASON_BADGE = {
    STORE_ON: { cls: 'status-processing-up', text: 'Tienda lo publica (on)' },
    STORE_OFF_CONFIRM: { cls: 'status-off', text: 'Emparejado (off)' },
    NO_STORE_STATUS: { cls: 'status-unselected', text: 'Sin estado en tienda' },
};

/**
 * MissingPublicationModal — Reportes "faltan publicar" por canal.
 * Props:
 *   show        boolean
 *   setShow     fn
 *   channel     string — alias del canal
 *   channels    array  — lista de canales (para label)
 *   account     string — cuenta seleccionada (vacío = todas)
 */
export default function MissingPublicationModal({ show, setShow, channel = '', channels = [], account = '' }) {
    const [activeTab, setActiveTab] = useState('resumen');

    // Resumen
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    // Detalle
    const [detailLoading, setDetailLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalsByReason, setTotalsByReason] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // Universo activo del detalle ('on' -> incompletos / 'off' -> faltan publicar)
    const [status, setStatus] = useState('off');
    // Filtros del detalle
    const [filtroCuenta, setFiltroCuenta] = useState('');
    const [filtroInk, setFiltroInk] = useState('');
    const [filtroValido, setFiltroValido] = useState('');
    const [filtroMotivo, setFiltroMotivo] = useState('');

    // Export
    const [exporting, setExporting] = useState(false);

    const channelLabel = channel
        ? (channels.find(c => c.alias === channel)?.name || channel)
        : channel;

    const buildBase = () =>
        `?channel=${encodeURIComponent(channel)}${account ? `&account=${encodeURIComponent(account)}` : ''}`;

    // Parámetros de universo + filtros del detalle
    const buildFilterQuery = (statusOverride) => {
        const st = statusOverride || status;
        let q = `&status=${encodeURIComponent(st)}`;
        q += `&only_invalid=${st === 'on' ? 'true' : 'false'}`;
        if (filtroCuenta) q += `&filtro_cuenta=${encodeURIComponent(filtroCuenta)}`;
        if (filtroInk) q += `&filtro_ink=${encodeURIComponent(filtroInk)}`;
        if (filtroValido) q += `&filtro_valido=${encodeURIComponent(filtroValido)}`;
        if (filtroMotivo) q += `&filtro_motivo=${encodeURIComponent(filtroMotivo)}`;
        return q;
    };

    const fetchSummary = useCallback(async () => {
        if (!channel) return;
        try {
            setSummaryLoading(true);
            const res = await fetch(`${urlTasks}${endpointMissingPublicationSummary}${buildBase()}`, {
                method: 'GET', headers: { 'Content-Type': 'application/json' }
            }).then(r => r.json());
            if (res && res.status === 'success') setSummary(res.data);
            else setSummary(null);
        } catch (e) {
            console.error('Error fetching summary:', e);
            setSummary(null);
        } finally {
            setSummaryLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, account]);

    const fetchDetail = useCallback(async (targetPage = 1, statusOverride) => {
        if (!channel) return;
        try {
            setDetailLoading(true);
            const res = await fetch(
                `${urlTasks}${endpointMissingPublication}${buildBase()}${buildFilterQuery(statusOverride)}&page=${targetPage}&per_page=${PER_PAGE}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            ).then(r => r.json());
            if (res && res.status === 'success') {
                setItems(res.data.items || []);
                setTotal(res.data.total || 0);
                setTotalPages(res.data.total_pages || 0);
                setTotalsByReason(res.data.totals_by_reason || []);
                setPage(targetPage);
            } else {
                setItems([]);
                setTotal(0);
                setTotalPages(0);
            }
        } catch (e) {
            console.error('Error fetching detail:', e);
            setItems([]);
            setTotal(0);
            setTotalPages(0);
        } finally {
            setDetailLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, account, status, filtroCuenta, filtroInk, filtroValido, filtroMotivo]);

    // Reset on open / close. Solo debe re-dispararse al abrir/cerrar el modal,
    // no al teclear en los filtros (por eso las deps no incluyen fetchDetail).
    useEffect(() => {
        if (show && channel) {
            setActiveTab('resumen');
            setPage(1);
            fetchSummary();
            fetchDetail(1);
        }
        if (!show) {
            setSummary(null);
            setItems([]);
            setTotal(0);
            setPage(1);
            setTotalPages(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, channel, account]);

    // Al cambiar de pestaña, recargar si es necesario
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'resumen' && !summary) fetchSummary();
        if (tab === 'detalle' && items.length === 0) fetchDetail(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages || detailLoading) return;
        fetchDetail(newPage);
    };

    // Descarga el XLSX directamente desde el backend
    const handleExportExcel = async () => {
        if (!channel) return;
        try {
            setExporting(true);
            const res = await fetch(`${urlTasks}${endpointMissingPublicationExport}${buildBase()}${buildFilterQuery()}`, {
                method: 'GET',
            });
            if (!res.ok) throw new Error(`Error ${res.status}`);
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `missing_publication_${channel}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Error exporting:', e);
            alert('Error al exportar Excel: ' + e.message);
        } finally {
            setExporting(false);
        }
    };

    const handleClose = () => setShow(false);
    const sTotals = summary?.totals || {};
    const totalFaltantes = sTotals.total_off ?? 0;

    // Selecciona un universo desde una card del resumen y va al detalle
    const selectUniverse = (value) => {
        setStatus(value);
        setActiveTab('detalle');
        setPage(1);
        fetchDetail(1, value);
    };

    // Recarga el detalle desde la página 1 al aplicar / limpiar filtros
    const aplicarFiltros = () => {
        setPage(1);
        fetchDetail(1);
    };

    const limpiarFiltros = () => {
        setFiltroCuenta('');
        setFiltroInk('');
        setFiltroValido('');
        setFiltroMotivo('');
        setPage(1);
        fetchDetail(1);
    };

    const tabs = [
        { value: 'resumen', label: 'Resumen', icon: faChartPie },
        { value: 'detalle', label: `Detalle${total ? ` (${total.toLocaleString()})` : ''}`, icon: faTable, sub: status === 'on' ? 'incompletos on' : 'off/disabled' },
        { value: 'excel', label: 'Exportar Excel', icon: faFileExcel },
    ];

    return (
        <Modal isOpen={show} onClose={handleClose} fullscreen>
            <div style={{ minHeight: 200 }}>
                {/* Header */}
                <div className="d-flex align-items-center gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
                    <span style={{ color: theme.primary, fontSize: '1.3rem' }}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                    </span>
                    <div>
                        <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>
                            Faltan publicar en tienda
                        </h4>
                        <p className="mb-0" style={{ color: 'rgba(var(--ink-overlay-rgb),0.55)', fontSize: '0.85rem' }}>
                            Canal: <strong style={{ color: 'var(--ink-text)' }}>{channelLabel || channel}</strong>
                            {account && (<> &nbsp;·&nbsp; Cuenta: <strong style={{ color: 'var(--ink-text)' }}>{account}</strong></>)}
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <nav className="d-flex gap-2 mb-3" style={{ borderBottom: '1px solid rgba(var(--ink-overlay-rgb),0.1)', paddingBottom: 10 }}>
                    {tabs.map(t => (
                        <button
                            key={t.value}
                            className="btn btn-sm d-flex align-items-center gap-2"
                            style={{
                                background: activeTab === t.value ? `rgba(\${theme.primaryRgb}, 0.15)` : 'rgba(var(--ink-overlay-rgb),0.05)',
                                color: activeTab === t.value ? theme.primary : 'rgba(var(--ink-overlay-rgb),0.7)',
                                border: activeTab === t.value ? `1px solid rgba(${theme.primaryRgb}, 0.5)` : '1px solid rgba(var(--ink-overlay-rgb),0.12)',
                                fontWeight: activeTab === t.value ? 700 : 400,
                            }}
                            onClick={() => handleTabChange(t.value)}
                        >
                            <FontAwesomeIcon icon={t.icon} />
                            {t.label}
                        </button>
                    ))}
                </nav>

                {/* ── PESTAÑA RESUMEN ─────────────────────────────────────── */}
                {activeTab === 'resumen' && (
                    <>
                        {summaryLoading && <Loading />}
                        {!summaryLoading && summary && (
                            <div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div
                                            className="card p-3 h-100"
                                            role="button"
                                            onClick={() => selectUniverse('on')}
                                            style={{
                                                background: 'var(--ink-surface-2)', border: `1px solid ${status === 'on' ? `rgba(\${theme.primaryRgb}, 0.6)` : 'rgba(var(--ink-overlay-rgb),0.1)'}`,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="small" style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)' }}>Títulos on · en tienda</div>
                                                    <div className="fs-3 fw-bold text-white">{(sTotals.total_on ?? 0).toLocaleString()}</div>
                                                    <div className="small" style={{ color: '#fbbf24' }}>
                                                        {((sTotals.on_invalid ?? 0)).toLocaleString()} incompletos
                                                    </div>
                                                </div>
                                                <span className="badge-status status-on" style={{ fontSize: '0.72rem', padding: '3px 8px' }}>ver incompletos</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div
                                            className="card p-3 h-100"
                                            role="button"
                                            onClick={() => selectUniverse('off')}
                                            style={{
                                                background: 'var(--ink-surface-2)', border: `1px solid ${status === 'off' ? 'rgba(245,158,11,0.6)' : 'rgba(var(--ink-overlay-rgb),0.1)'}`,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="small" style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)' }}>Faltan publicar (off/disabled)</div>
                                                    <div className="fs-3 fw-bold text-warning">{totalFaltantes.toLocaleString()}</div>
                                                    <div className="small" style={{ color: 'rgba(var(--ink-overlay-rgb),0.6)' }}>
                                                        {((sTotals.off_valid ?? 0)).toLocaleString()} completos ·{' '}
                                                        <span style={{ color: '#fbbf24' }}>{((sTotals.off_invalid ?? 0)).toLocaleString()} incompletos</span>
                                                    </div>
                                                </div>
                                                <span className="badge-status status-off" style={{ fontSize: '0.72rem', padding: '3px 8px' }}>ver detalles</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {summary.report_loaded?.loaded && (
                                    <div className="mb-2 small" style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)' }}>
                                        Reporte cargado: {summary.report_loaded.total_records?.toLocaleString()} registros ·{' '}
                                        {summary.report_loaded.upload_date ? new Date(summary.report_loaded.upload_date).toLocaleString() : ''}
                                    </div>
                                )}

                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.85rem', padding: '5px 12px' }}>
                                        {totalFaltantes.toLocaleString()} título(s) no publicados en tienda
                                    </span>
                                </div>
                            </div>
                        )}
                        {!summaryLoading && !summary && (channel ? <p style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)' }}>No se pudo cargar el resumen.</p> : <p style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)' }}>Selecciona una tienda.</p>)}
                    </>
                )}

                {/* ── PESTAÑA DETALLE ─────────────────────────────────────── */}
                {activeTab === 'detalle' && (
                    <div>
                        {/* Selector de universo + filtros */}
                        <div className="d-flex align-items-center justify-content-between mb-3" style={{ flexWrap: 'wrap', gap: 10 }}>
                            <div className="btn-group btn-group-sm" role="group">
                                <button type="button" className="btn" style={{
                                    background: status === 'on' ? `rgba(\${theme.primaryRgb}, 0.18)` : 'rgba(var(--ink-overlay-rgb),0.05)',
                                    color: status === 'on' ? theme.primary : 'rgba(var(--ink-overlay-rgb),0.7)',
                                    border: `1px solid ${status === 'on' ? `rgba(\${theme.primaryRgb}, 0.5)` : 'rgba(var(--ink-overlay-rgb),0.12)'}`,
                                }} onClick={() => selectUniverse('on')}>
                                    Incompletos on
                                </button>
                                <button type="button" className="btn" style={{
                                    background: status === 'off' ? 'rgba(245,158,11,0.18)' : 'rgba(var(--ink-overlay-rgb),0.05)',
                                    color: status === 'off' ? '#fbbf24' : 'rgba(var(--ink-overlay-rgb),0.7)',
                                    border: `1px solid ${status === 'off' ? 'rgba(245,158,11,0.5)' : 'rgba(var(--ink-overlay-rgb),0.12)'}`,
                                }} onClick={() => selectUniverse('off')}>
                                    Faltan publicar (off/disabled)
                                </button>
                            </div>
                            <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)', fontSize: '0.82rem' }}>
                                {status === 'on' ? 'Títulos on incompletos' : 'Títulos off/disabled no publicados'}
                            </span>
                        </div>

                        <div className="d-flex align-items-end gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)', fontSize: '0.72rem' }}>Cuenta</label>
                                <input className="form-control form-control-sm" placeholder="Cuenta"
                                    style={{ width: 140, background: theme.surfaceRaised, color: 'var(--ink-text)', border: '1px solid rgba(var(--ink-overlay-rgb),0.2)' }}
                                    value={filtroCuenta} onChange={e => setFiltroCuenta(e.target.value)} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)', fontSize: '0.72rem' }}>Ink ID</label>
                                <input className="form-control form-control-sm" placeholder="Ink ID"
                                    style={{ width: 120, background: theme.surfaceRaised, color: 'var(--ink-text)', border: '1px solid rgba(var(--ink-overlay-rgb),0.2)' }}
                                    value={filtroInk} onChange={e => setFiltroInk(e.target.value)} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)', fontSize: '0.72rem' }}>Válido</label>
                                <select className="form-select form-select-sm" style={{ width: 110, background: theme.surfaceRaised, color: 'var(--ink-text)', border: '1px solid rgba(var(--ink-overlay-rgb),0.2)' }}
                                    value={filtroValido} onChange={e => setFiltroValido(e.target.value)}>
                                    <option value="">Todos</option>
                                    <option value="si">Sí</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label style={{ color: 'rgba(var(--ink-overlay-rgb),0.5)', fontSize: '0.72rem' }}>Motivo</label>
                                <select className="form-select form-select-sm" style={{ width: 200, background: theme.surfaceRaised, color: 'var(--ink-text)', border: '1px solid rgba(var(--ink-overlay-rgb),0.2)' }}
                                    value={filtroMotivo} onChange={e => setFiltroMotivo(e.target.value)}>
                                    <option value="">Todos</option>
                                    {Object.keys(REASON_BADGE).map(r => (
                                        <option key={r} value={r}>{REASON_BADGE[r].text}</option>
                                    ))}
                                </select>
                            </div>
                            <button className="btn btn-sm btn-success" onClick={aplicarFiltros}>Aplicar</button>
                            <button className="btn btn-sm" style={{ background: 'rgba(var(--ink-overlay-rgb),0.08)', color: 'var(--ink-text)', border: '1px solid rgba(var(--ink-overlay-rgb),0.15)' }} onClick={limpiarFiltros}>Limpiar</button>
                        </div>

                        {/* Conteo por razón */}
                        {totalsByReason.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {totalsByReason.map((r, i) => {
                                    const badge = REASON_BADGE[r.reason] || { cls: 'status-unselected', text: r.reason };
                                    return (
                                        <span key={i} className={`badge-status ${badge.cls}`} style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                                            title={r.label}>
                                            {badge.text}: {r.count.toLocaleString()}
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {detailLoading && <Loading />}

                        {!detailLoading && items.length === 0 && (
                            <div className="text-center py-5" style={{ color: 'rgba(var(--ink-overlay-rgb),0.45)' }}>
                                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
                                <p>
                                    {status === 'on'
                                        ? 'No hay títulos on incompletos para esta selección.'
                                        : 'No hay títulos off/disabled sin publicar para esta selección.'}
                                </p>
                            </div>
                        )}

                        {!detailLoading && items.length > 0 && (
                            <>
                                <div className="modern-table-container">
                                    <table className="modern-table table-hover">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 40 }}>#</th>
                                                <th style={{ minWidth: 200 }}>Título</th>
                                                <th>Cuenta</th>
                                                <th>Ink ID</th>
                                                <th style={{ minWidth: 130 }}>ISBN</th>
                                                <th>Válido</th>
                                                <th>Motivo</th>
                                                <th style={{ minWidth: 200 }}>Campos faltantes / acciones</th>
                                                <th>Stock / Tienda</th>
                                                <th style={{ minWidth: 140 }}>Envío</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, idx) => {
                                                const badge = REASON_BADGE[item.reason] || { cls: 'status-unselected', text: item.reason };
                                                const missing = item.missing_fields || [];
                                                return (
                                                    <tr key={idx}>
                                                        <td style={{ color: 'rgba(var(--ink-overlay-rgb),0.35)', fontSize: '0.8rem' }}>{(page - 1) * PER_PAGE + idx + 1}</td>
                                                        <td style={{ fontSize: '0.85rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.title || undefined}>
                                                            {item.title || <span style={{ opacity: 0.35 }}>—</span>}
                                                        </td>
                                                        <td style={{ fontWeight: 600, color: theme.primary, fontSize: '0.85rem' }}>{item.account || '—'}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{item.ink_id != null ? item.ink_id : '—'}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{item.isbn || '—'}</td>
                                                        <td className="text-center">
                                                            <span className="badge-status" style={{
                                                                fontSize: '0.72rem', padding: '3px 8px',
                                                                background: item.valid ? `rgba(\${theme.primaryRgb}, 0.15)` : 'rgba(245,158,11,0.15)',
                                                                color: item.valid ? theme.primary : '#fbbf24',
                                                                border: `1px solid ${item.valid ? `rgba(\${theme.primaryRgb}, 0.4)` : 'rgba(245,158,11,0.4)'}`,
                                                            }}>
                                                                {item.valid ? 'OK' : 'Falta'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge-status ${badge.cls}`} style={{ fontSize: '0.72rem', padding: '3px 8px' }} title={item.reason_label}>
                                                                {badge.text}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: '0.78rem' }}>
                                                            {missing.length > 0 ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                                    {missing.map((f, i) => (
                                                                        <span key={i} style={{
                                                                            background: f.warning ? `rgba(\${theme.primaryRgb}, 0.12)` : 'rgba(245,158,11,0.15)',
                                                                            color: f.warning ? theme.primary : '#fbbf24',
                                                                            border: `1px solid ${f.warning ? `rgba(\${theme.primaryRgb}, 0.35)` : 'rgba(245,158,11,0.3)'}`,
                                                                            borderRadius: 4, padding: '2px 6px', fontSize: '0.72rem', whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {f.warning ? '⚠ ' : ''}{f.field}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span style={{ opacity: 0.35 }}>—</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="badge-status status-off" style={{ fontSize: '0.72rem', padding: '3px 8px' }}>
                                                                {item.channel_status || 'off'}
                                                            </span>
                                                            <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', margin: '0 4px', fontSize: '0.7rem' }}>/</span>
                                                            <span className={statusBadgeClass(item.store_status)} style={{ fontSize: '0.72rem', padding: '3px 8px' }}>
                                                                {item.store_status || '—'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: '0.72rem' }}>
                                                            {item.send_state ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                    <span className="badge-status" style={{
                                                                        alignSelf: 'flex-start', fontSize: '0.72rem', padding: '3px 8px',
                                                                        background: item.send_state === 'proceso' ? `rgba(\${theme.primaryRgb}, 0.15)` : 'rgba(245,158,11,0.15)',
                                                                        color: item.send_state === 'proceso' ? theme.primary : '#fbbf24',
                                                                        border: `1px solid ${item.send_state === 'proceso' ? `rgba(\${theme.primaryRgb}, 0.4)` : 'rgba(245,158,11,0.4)'}`,
                                                                    }}>
                                                                        {item.send_state === 'proceso' ? 'En proceso' : 'Pendiente de envío'}
                                                                    </span>
                                                                    {item.notification_date && (
                                                                        <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.45)' }}>
                                                                            {new Date(item.notification_date).toLocaleString()}
                                                                        </span>
                                                                    )}
                                                                    {item.notification_type && (
                                                                        <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.45)' }}>
                                                                            Tipo: {item.notification_type} · done: {item.notification_done}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ opacity: 0.35 }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Paginación */}
                                <div className="d-flex justify-content-between align-items-center gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
                                    <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.55)', fontSize: '0.82rem' }}>
                                        Mostrando{' '}
                                        <strong style={{ color: 'var(--ink-text)' }}>{total === 0 ? 0 : (page - 1) * PER_PAGE + 1}</strong>
                                        {' – '}
                                        <strong style={{ color: 'var(--ink-text)' }}>{Math.min(page * PER_PAGE, total)}</strong>
                                        {' de '}
                                        <strong style={{ color: 'var(--ink-text)' }}>{total.toLocaleString()}</strong>
                                    </span>
                                    {totalPages > 1 && (
                                        <div className="d-flex align-items-center gap-2">
                                            <button className="btn btn-sm" style={{
                                                background: page <= 1 ? 'rgba(var(--ink-overlay-rgb),0.05)' : 'rgba(var(--ink-overlay-rgb),0.12)',
                                                color: page <= 1 ? 'rgba(var(--ink-overlay-rgb),0.25)' : 'var(--ink-text)',
                                                border: '1px solid rgba(var(--ink-overlay-rgb),0.15)', padding: '3px 10px', fontSize: '0.8rem',
                                                cursor: page <= 1 ? 'not-allowed' : 'pointer',
                                            }} onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || detailLoading}>
                                                ← Anterior
                                            </button>
                                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem', color: 'rgba(var(--ink-overlay-rgb),0.7)' }}>
                                                <span>Pág.</span>
                                                <select className="form-select form-select-sm" style={{
                                                    width: 70, background: theme.surfaceRaised, color: 'var(--ink-text)', border: '1px solid rgba(var(--ink-overlay-rgb),0.2)',
                                                    fontSize: '0.8rem', padding: '2px 4px',
                                                }} value={page} onChange={e => handlePageChange(Number(e.target.value))} disabled={detailLoading}>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                                        <option key={p} value={p}>{p}</option>
                                                    ))}
                                                </select>
                                                <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)' }}>de {totalPages}</span>
                                            </div>
                                            <button className="btn btn-sm" style={{
                                                background: page >= totalPages ? 'rgba(var(--ink-overlay-rgb),0.05)' : 'rgba(var(--ink-overlay-rgb),0.12)',
                                                color: page >= totalPages ? 'rgba(var(--ink-overlay-rgb),0.25)' : 'var(--ink-text)',
                                                border: '1px solid rgba(var(--ink-overlay-rgb),0.15)', padding: '3px 10px', fontSize: '0.8rem',
                                                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                                            }} onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages || detailLoading}>
                                                Siguiente →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── PESTAÑA EXCEL ───────────────────────────────────────── */}
                {activeTab === 'excel' && (
                    <div className="text-center py-5" style={{ color: 'rgba(var(--ink-overlay-rgb),0.7)' }}>
                        <div style={{ fontSize: '3rem', marginBottom: 12 }}>
                            <FontAwesomeIcon icon={faFileExcel} style={{ color: '#22c55e' }} />
                        </div>
                        <p className="mb-1">
                            Descarga el listado completo (todas las páginas) de títulos que faltan publicar en{' '}
                            <strong style={{ color: 'var(--ink-text)' }}>{channelLabel || channel}</strong>.
                        </p>
                        <p style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', fontSize: '0.85rem' }}>Formato Excel (.xlsx)</p>
                        <button
                            className="btn btn-success px-4 d-inline-flex align-items-center gap-2"
                            onClick={handleExportExcel}
                            disabled={exporting}
                        >
                            <FontAwesomeIcon icon={faDownload} spin={exporting} />
                            {exporting ? 'Descargando…' : 'Descargar Excel'}
                        </button>
                    </div>
                )}

                {/* Footer */}
                <div className="d-flex justify-content-end mt-4 pt-3 border-top border-secondary">
                    <button className="btn btn-secondary px-4" onClick={handleClose}>Cerrar</button>
                </div>
            </div>
        </Modal>
    );
}
